// dashboardMockData.ts
import { ICON_BASE_PATH } from "@/app/utils/config";

// ---------------- Types ----------------

export interface CashFlowSummary {
  inflow: number;
  outflow: number;
  netPosition: number;
  periodLabel: string;
}

export interface AccountBalanceItem {
  id: string;
  accountName: string;
  accountNumber: string;
  balance: number;
  currency: string;
  percentage: number;
  type: "operating" | "reserve" | "liability";
}

export interface ObligationItem {
  id: string;
  date: string;
  title: string;
  amount: number;
  currency: string;
  status: "upcoming" | "overdue" | "paid";
  dueInDays?: number;
}

export interface QuickActionItem {
  id: string;
  label: string;
  icon: string;
  route: string;
}

// ---------------- Mock Data ----------------

export const mockCashFlowSummary: CashFlowSummary = {
  inflow: 80600,
  outflow: -12600,
  netPosition: 62600,
  periodLabel: "June",
};

export const mockAccountBalances: AccountBalanceItem[] = [
  {
    id: "acc-general",
    accountName: "General Account",
    accountNumber: "***1234",
    balance: 631300,
    currency: "AED",
    percentage: 78,
    type: "operating",
  },
  {
    id: "acc-savings",
    accountName: "Savings Account",
    accountNumber: "***1234",
    balance: 231300,
    currency: "AED",
    percentage: 32,
    type: "reserve",
  },
  {
    id: "acc-credit-card",
    accountName: "Credit Card",
    accountNumber: "***1234",
    balance: -31300.16,
    currency: "AED",
    percentage: 18,
    type: "liability",
  },
];

export const mockObligations: ObligationItem[] = [
  {
    id: "ob-1",
    date: "2026-06-15",
    title: "Credit Card ****1234",
    amount: 18120,
    currency: "AED",
    status: "upcoming",
    dueInDays: 2,
  },
  {
    id: "ob-2",
    date: "2026-06-15",
    title: "Vendor Payroll Batch",
    amount: 18120,
    currency: "AED",
    status: "upcoming",
  },
  {
    id: "ob-3",
    date: "2026-06-15",
    title: "Loan Installment",
    amount: 18120,
    currency: "AED",
    status: "upcoming",
  },
  {
    id: "ob-4",
    date: "2026-06-15",
    title: "Office Rent",
    amount: 18120,
    currency: "AED",
    status: "upcoming",
  },
];

export const mockQuickActions: QuickActionItem[] = [
  {
    id: "qa-beneficiaries",
    label: "Manage Beneficiaries",
    icon: `${ICON_BASE_PATH}san-manage-beneficiaries.svg`,
    route: "/beneficiaries/manageBeneficiary/",
  },
  {
    id: "qa-admin",
    label: "Admin Menu",
    icon: `${ICON_BASE_PATH}san-adminmenu.svg`,
    route: "/dashboard/admin-menu",
  },
  {
    id: "qa-scheduled-transfers",
    label: "Scheduled Transfers",
    icon: `${ICON_BASE_PATH}san-scheduled-transfers.svg`,
    route: "/payments/scheduledTransfer",
  },
  {
    id: "qa-new-payment",
    label: "New Payment",
    icon: `${ICON_BASE_PATH}pay-invoices.svg`,
    route: "/payments/initiateANewTransfer",
  },
];

export const mockInfoCarouselItems = [
  {
    title: "3 bill payments scheduled for tomorrow",
    description:
      "Electricity, Internet, and Mobile bills will be debited from *Ria21-Brazil **1101. Please ensure sufficient balance.",
  },
  {
    title: "One loan is nearing maturity",
    description:
      "Your Ria21-109 Mortgage Loan linked to your primary business account is approaching its maturity period.",
  },
  {
    title: "Card payment due in 2 days",
    description:
      "An amount of €18,450 is due on your Credit Card ** ** ** 9191. Please make the payment to avoid late charges.",
  },
  {
    title: "Low balance detected in one account",
    description:
      "Your *Ria21-Brazil **1101 balance is below the required level for upcoming scheduled payments.",
  },
];

// ---------------- Cash Position (chart) mock data ----------------

export interface CashPositionPoint {
  name: string;
  value: number;
}

export type CashPositionPeriod = "30D" | "90D" | "1Y";

export const mockCashPositionData: Record<
  CashPositionPeriod,
  CashPositionPoint[]
> = {
  "30D": [
    { name: "17 May", value: 620000 },
    { name: "20 May", value: 540000 },
    { name: "23 May", value: 480000 },
    { name: "26 May", value: 610000 },
    { name: "29 May", value: 780000 },
    { name: "1 Jun", value: 820000 },
    { name: "4 Jun", value: 690000 },
    { name: "7 Jun", value: 640000 },
    { name: "10 Jun", value: 700000 },
    { name: "13 Jun", value: 660000 },
    { name: "15 Jun", value: 720000 },
  ],
  "90D": [
    { name: "Apr W1", value: 410000 },
    { name: "Apr W2", value: 520000 },
    { name: "Apr W3", value: 470000 },
    { name: "Apr W4", value: 610000 },
    { name: "May W1", value: 590000 },
    { name: "May W2", value: 680000 },
    { name: "May W3", value: 750000 },
    { name: "May W4", value: 700000 },
    { name: "Jun W1", value: 690000 },
    { name: "Jun W2", value: 720000 },
  ],
  "1Y": [
    { name: "Jul", value: 320000 },
    { name: "Aug", value: 380000 },
    { name: "Sep", value: 410000 },
    { name: "Oct", value: 390000 },
    { name: "Nov", value: 460000 },
    { name: "Dec", value: 520000 },
    { name: "Jan", value: 480000 },
    { name: "Feb", value: 550000 },
    { name: "Mar", value: 610000 },
    { name: "Apr", value: 650000 },
    { name: "May", value: 700000 },
    { name: "Jun", value: 720000 },
  ],
};
